import { DEFAULT_LOOK_ID } from 'product/constants/constants'
import { getClientProductPriceUrl } from 'product/services/productPrices/getPricesUrl'
import { type ProductPrices } from 'product/types/Prices'
import type { ProductColor } from 'product/types/Product'
import { type MasterData } from 'types/masterData'

const NO_PERSONALIZED = 'no-personalized'

export interface Item {
	productId: string
	colorId: string
	quantity: number
	lookId?: string
	index?: number
	list?: string
	personalized?: string
	sizeId?: string
	coupon?: string
	seller?: string
	look?: string
}

export interface ItemWithPrices extends Item {
	currentPrice: number
	originalPrice: number
}

export interface EECItem {
	item_id: string
	item_category3: string
	item_category4: boolean
	item_list_id?: string
	item_list_name?: string
	item_variant: string
	index?: number
	quantity: number
	current_price: number
	original_price: number
	item_category2?: string
	coupon?: string
}

export const getEECItemsAsync = async (
	masterData: MasterData,
	items: Item[]
): Promise<EECItem[]> => {
	const {
		country: { countryISO },
		channel,
	} = masterData

	const fetchProductPrices = async (
		productId: string
	): Promise<Record<ProductColor['id'], ProductPrices>> => {
		const pricesResponse = await fetch(
			getClientProductPriceUrl({ countryISO, channel, productId }),
			{ next: { revalidate: 0 } }
		)
		return pricesResponse.json()
	}

	const mapItemToEEC = async (item: Item): Promise<EECItem> => {
		const { productId, colorId } = item
		const prices = await fetchProductPrices(productId)

		return itemToEEC({
			...item,
			currentPrice: prices?.[colorId]?.price,
			originalPrice:
				prices?.[colorId]?.previousPrices?.originalShop ||
				prices?.[colorId]?.price,
		})
	}

	return Promise.all(items.map(mapItemToEEC))
}

const itemToEEC = ({
	productId,
	sizeId,
	lookId = DEFAULT_LOOK_ID,
	personalized,
	list,
	colorId,
	index,
	quantity,
	currentPrice,
	originalPrice,
	coupon,
}: ItemWithPrices): EECItem => ({
	index,
	quantity,
	coupon,
	item_id: productId,
	item_category2: sizeId,
	item_category3: lookId,
	item_category4: personalized !== NO_PERSONALIZED,
	item_list_id: list,
	item_list_name: list,
	item_variant: colorId,
	current_price: currentPrice,
	original_price: originalPrice,
})

export const getEECItems = (items: ItemWithPrices[]): EECItem[] =>
	items.map(itemToEEC)
